import { ReactNode, FC } from "react";
import { AntDesignCheckbox, Space, Tooltip, Row, Col, Typography, Grid } from "@ctra/components";
import { LoadingOutlined, CompassOutlined } from "@ant-design/icons";
import moment from "moment";
import * as _ from "lodash";

import { FarmEntity, GamePlanEntity, TodoItemEntity, TodoItemStatus, TodoUpdateAction } from "@ctra/api";
import { classname, TS } from "@ctra/utils";

import { Icon, Barn } from "../../../../elements/Icon";
import styles from "./Task.module.less";

/**
 * Task variant
 */
export enum TaskVariant {
  Expanded = "expanded",
  Inline = "inline"
}

export type TaskProps = {
  /**
   * Variant to render
   */
  variant: TaskVariant;
  /**
   * Farm name
   */
  farm?: FarmEntity["name"];
  /**
   * game plan name
   */
  gamePlan?: GamePlanEntity["title"];
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Change handler
   */
  onChange?: () => void;
  /**
   * Extra (buttons) to render
   */
  extra?: ReactNode;
} & TodoItemEntity;

const { Text } = Typography;

/**
 * Inline game plan task
 * @param loading
 * @param onChange
 * @param item
 * @constructor
 */
const InlineTask: FC<Omit<TaskProps, "variant">> = ({ loading, onChange, ...item }) => (
  <Tooltip title={item.label} key={item.id}>
    <AntDesignCheckbox
      className={styles.Checkbox}
      checked={item.status === TodoItemStatus.Completed}
      disabled={loading}
      onClick={onChange}
    />
  </Tooltip>
);

/**
 * Custom checkbox for a task
 * @param {Omit<{variant: TaskVariant, farm?: FarmEntity["name"], gamePlan?: GamePlanEntity["title"], loading?: boolean, onChange?: () => void, extra?: React.ReactNode} & TodoItemEntity, "variant">} item
 * @param {boolean} loading
 * @param {() => void} onChange
 * @return {JSX.Element}
 * @constructor
 */
export const TaskCheckbox: FC<{
  item: Omit<TaskProps, "variant">;
  loading?: boolean;
  onChange?: () => void;
  className?: string;
}> = ({ item, loading, onChange, className }) => (
  <AntDesignCheckbox
    className={classname(styles.Checkbox, className)}
    checked={item.status === TodoItemStatus.Completed}
    disabled={loading}
    onClick={onChange}
  />
);

/**
 * Expanded task
 * @param loading
 * @param onChange
 * @param extra
 * @param farm
 * @param gamePlan
 * @param children
 * @param item
 * @constructor
 */
const ExpandedTask: FC<Omit<TaskProps, "variant">> = ({
  loading,
  onChange,
  extra,
  farm,
  gamePlan,
  children,
  ...item
}) => {
  const { md } = Grid.useBreakpoint();

  const isOverdue = item.dueDateTs && TS.asMoment(item.dueDateTs).isBefore(moment());

  /**
   * Make the extras column responsive
   */
  const responsiveExtras = md
    ? {
        flex: "none"
      }
    : {
        span: 24
      };

  /**
   * Loading indicator
   */
  const spinner = <LoadingOutlined style={{ visibility: loading ? "visible" : "hidden" }} />;

  return (
    <Row gutter={0} className={classname(isOverdue ? styles.Overdue : "", styles.Wrapper)} wrap={!md}>
      <Col flex="auto">
        <Row wrap={false}>
          <TaskCheckbox
            className={styles.Margin}
            key={item.id}
            item={item}
            loading={loading}
            onChange={onChange}
          />
          {farm || gamePlan ? (
            <Space direction="vertical" size={4}>
              <Text>{item.label}</Text>
              {farm && (
                <Text type="secondary" className={styles.Sub}>
                  <Icon component={Barn} /> {farm}
                </Text>
              )}
              {gamePlan && (
                <Text type="secondary" className={styles.Sub}>
                  <CompassOutlined /> {gamePlan}
                </Text>
              )}
            </Space>
          ) : (
            <Text>{item.label}</Text>
          )}
        </Row>
      </Col>
      <Col flex="none" className={styles.Loading}>
        {spinner}
      </Col>
      {extra && (
        <Col className={styles.Extra} {...responsiveExtras}>
          {extra}
          <Row justify="end">{children}</Row>
        </Col>
      )}
    </Row>
  );
};

/**
 * Game plan related task
 * @param variant
 * @param onChange
 * @param props
 * @constructor
 */
const Task: FC<TaskProps> = ({ variant, onChange = _.noop, ...props }) => {
  const advance = _.partial(onChange, TodoUpdateAction.advance);

  return variant === TaskVariant.Inline ? (
    <InlineTask onChange={advance} {...props} />
  ) : (
    <ExpandedTask onChange={advance} {...props} />
  );
};

export default Task;
